import { create } from 'zustand';
import i18n from '../i18n';
import { getTenantId } from './authStore';
import {
  PIVOT_PAGE_SIZE, PIVOT_VALUE_CAP, PivotCrumb, PivotOption, RawOntologyLink,
  buildPivotFilter, distinctValuesBody, keyValuesFromGroups, pushPivotCrumb,
  toWireFilters, truncateTrail,
} from '../modules/explorer/linkedPivot';

const ANALYTICS_API = import.meta.env.VITE_ANALYTICS_SERVICE_URL || 'http://localhost:8015';
const ONTOLOGY_API = import.meta.env.VITE_ONTOLOGY_SERVICE_URL || 'http://localhost:8004';

export interface FilterRow {
  id: string;
  field: string;
  op: string;
  value: string;
  /** Solo con op 'in': los valores de enlace del pivote "Ver vinculados". */
  values?: string[];
  /** Solo con op 'in': de qué conjunto vienen los valores (para mostrarlo). */
  pivotFrom?: string;
}

export interface AggregateSpec {
  function: 'COUNT' | 'COUNT_DISTINCT' | 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'RUNTIME';
  field: string;
  ts_field?: string;
}

export interface ExplorerObjectType {
  id: string;
  name: string;
  displayName: string;
}

export interface ExploreResult {
  rows: Record<string, unknown>[];
  total: number;
  columns: string[];
  query_ms: number;
}

export interface AnalystResult {
  answer: string;
  query_used: Record<string, unknown> | null;
  rows: Record<string, unknown>[] | null;
  columns: string[] | null;
  total: number | null;
}

interface ExplorerStore {
  objectTypes: ExplorerObjectType[];
  selectedTypeId: string | null;
  fields: string[];
  recordCount: number;
  filters: FilterRow[];
  aggregate: AggregateSpec | null;
  groupBy: string | null;
  result: ExploreResult | null;
  analystResult: AnalystResult | null;
  loading: boolean;
  loadingFields: boolean;
  loadingAnalyst: boolean;
  /** Vínculos de la ontología (GET /object-types/links/all), null hasta cargarlos. */
  links: RawOntologyLink[] | null;
  /** Migas del pivote a vinculados; vacío cuando no se ha pivotado. */
  trail: PivotCrumb[];
  pivoting: boolean;
  pivotError: string | null;

  fetchObjectTypes: () => Promise<void>;
  selectObjectType: (id: string) => Promise<void>;
  setFilters: (filters: FilterRow[]) => void;
  setAggregate: (agg: AggregateSpec | null) => void;
  setGroupBy: (field: string | null) => void;
  runQuery: (limit?: number, offset?: number) => Promise<void>;
  runAnalyst: (question: string) => Promise<void>;
  exportQuery: (format: 'csv' | 'xlsx', checkpointToken?: string) => Promise<Blob>;
  clearResult: () => void;
  fetchLinks: () => Promise<void>;
  pivotToLinked: (option: PivotOption, via: string) => Promise<void>;
  restoreCrumb: (index: number) => Promise<void>;
  clearTrail: () => void;
}

/** Valores distintos de `field` en el conjunto (typeId + filters), hasta el tope. */
async function collectKeyValues(
  typeId: string, filters: FilterRow[], field: string,
): Promise<{ values: Set<string>; truncated: boolean }> {
  const values = new Set<string>();
  let offset = 0;
  for (;;) {
    const r = await fetch(`${ANALYTICS_API}/explore/query`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(distinctValuesBody(typeId, filters, field, offset)),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      const detail = (err as { detail?: unknown }).detail;
      const msg = typeof detail === 'string' ? detail : (detail as { message?: string } | undefined)?.message;
      throw new Error(msg || `HTTP ${r.status}`);
    }
    const data = await r.json();
    const rows: Record<string, unknown>[] = Array.isArray(data?.rows) ? data.rows : [];
    const before = values.size;
    keyValuesFromGroups(rows, values);
    if (values.size >= PIVOT_VALUE_CAP) {
      return { values: new Set(Array.from(values).slice(0, PIVOT_VALUE_CAP)), truncated: true };
    }
    // Página incompleta = no hay más grupos. Sin valores nuevos = la fuente no
    // pagina (tipos federados): seguir repetiría la misma página.
    if (rows.length < PIVOT_PAGE_SIZE || values.size === before) break;
    offset += rows.length;
  }
  return { values, truncated: false };
}

export class ExportError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ExportError';
  }
}

export const useExplorerStore = create<ExplorerStore>((set, get) => ({
  objectTypes: [],
  selectedTypeId: null,
  fields: [],
  recordCount: 0,
  filters: [],
  aggregate: null,
  groupBy: null,
  result: null,
  analystResult: null,
  loading: false,
  loadingFields: false,
  loadingAnalyst: false,
  links: null,
  trail: [],
  pivoting: false,
  pivotError: null,

  fetchObjectTypes: async () => {
    try {
      const r = await fetch(`${ANALYTICS_API}/explore/object-types`, {
        headers: { 'x-tenant-id': getTenantId() },
      });
      if (r.ok) {
        const data = await r.json();
        set({ objectTypes: Array.isArray(data) ? data : [] });
      }
    } catch {
      set({ objectTypes: [] });
    }
  },

  selectObjectType: async (id) => {
    set({ selectedTypeId: id, fields: [], recordCount: 0, result: null, analystResult: null, loadingFields: true });
    try {
      const r = await fetch(`${ANALYTICS_API}/explore/object-types/${id}/fields`, {
        headers: { 'x-tenant-id': getTenantId() },
      });
      if (r.ok) {
        const data = await r.json();
        // The API answers with the type's schema (plus any extra key the rows
        // carry), already narrowed to the caller's field grant: use it as is.
        // A slow answer for a type the user already left must not overwrite
        // the field list of the one now open.
        if (get().selectedTypeId !== id) return;
        set({ fields: data.fields || [], recordCount: data.record_count || 0 });
      }
    } catch {
      if (get().selectedTypeId === id) set({ fields: [] });
    } finally {
      set({ loadingFields: false });
    }
  },

  setFilters: (filters) => set({ filters }),
  setAggregate: (aggregate) => set({ aggregate }),
  setGroupBy: (groupBy) => set({ groupBy }),

  runQuery: async (limit = 100, offset = 0) => {
    const { selectedTypeId, filters, aggregate, groupBy, result } = get();
    if (!selectedTypeId) return;

    // The total-row COUNT(*) is a full scan of the partition and is the main
    // cost on large datasets. We only need it to size the pager, so ask for it
    // on the first page (offset 0) and reuse the cached total on page-flips.
    const withCount = offset === 0;

    set({ loading: true });
    try {
      const body: Record<string, unknown> = {
        object_type_id: selectedTypeId,
        filters: toWireFilters(filters),
        limit,
        offset,
        with_count: withCount,
      };
      if (aggregate && groupBy) {
        body.aggregate = aggregate;
        body.group_by = groupBy;
      }

      const r = await fetch(`${ANALYTICS_API}/explore/query`, {
        method: 'POST',
        headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const data = await r.json();
        // When the count was skipped the backend returns total: null —
        // carry forward the total from the last query so the pager is stable.
        if (data.total == null) {
          data.total = result?.total ?? get().recordCount;
        }
        set({ result: data });
      }
    } catch {
      // keep last result
    } finally {
      set({ loading: false });
    }
  },

  // The file is built by analytics-service, which redeems the single-use
  // checkpoint token with audit-service before reading any row. Without a
  // valid token (when a data_export checkpoint applies) the server answers 403.
  exportQuery: async (format, checkpointToken) => {
    const { selectedTypeId, filters, aggregate, groupBy } = get();
    if (!selectedTypeId) throw new Error('no object type selected');
    const body: Record<string, unknown> = {
      object_type_id: selectedTypeId,
      filters: toWireFilters(filters),
      format,
    };
    if (aggregate && groupBy) {
      body.aggregate = aggregate;
      body.group_by = groupBy;
    }
    const headers: Record<string, string> = {
      'x-tenant-id': getTenantId(),
      'Content-Type': 'application/json',
    };
    if (checkpointToken) headers['X-Checkpoint-Token'] = checkpointToken;
    const r = await fetch(`${ANALYTICS_API}/explore/export`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      const detail = (err as { detail?: unknown }).detail;
      const message = typeof detail === 'string'
        ? detail
        : (detail as { message?: string } | undefined)?.message;
      throw new ExportError(r.status, message || `HTTP ${r.status}`);
    }
    return r.blob();
  },

  runAnalyst: async (question) => {
    const { selectedTypeId, objectTypes } = get();
    if (!selectedTypeId || !question.trim()) return;

    const type = objectTypes.find((t) => t.id === selectedTypeId);
    set({ loadingAnalyst: true, analystResult: null });
    try {
      // Authorization NO se pone aquí a mano: window.fetch está envuelto por
      // makeAuthFetch (main.tsx), que lo inyecta y además reintenta una vez tras
      // refrescar el token si sale 401. Ponerlo aquí lo fijaría al token viejo y
      // el reintento volvería a fallar — `send()` respeta el header ya presente.
      const r = await fetch(`${ANALYTICS_API}/analyst/query`, {
        method: 'POST',
        headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          object_type_id: selectedTypeId,
          object_type_name: type?.displayName || type?.name,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        set({ analystResult: data });
      } else {
        // 403: the Explorer's doors (project role, the type's view grant). Shown
        // as the answer, with the server's reason when it says more.
        const body = (await r.json().catch(() => null)) as { detail?: unknown } | null;
        const detail = typeof body?.detail === 'string' ? body.detail : '';
        const answer = r.status === 403
          ? i18n.t('explorer.aipAnalystDenied', { defaultValue: 'No tienes permiso para consultar este tipo de objeto con el Analista AIP.' })
            + (detail && !detail.startsWith('Permission denied') ? `\n${detail}` : '')
          : detail || `HTTP ${r.status}`;
        set({ analystResult: { answer, query_used: null, rows: null, columns: null, total: null } });
      }
    } catch {
      set({ analystResult: { answer: 'Failed to connect to analytics service.', query_used: null, rows: null, columns: null, total: null } });
    } finally {
      set({ loadingAnalyst: false });
    }
  },

  clearResult: () => set({ result: null, analystResult: null }),

  fetchLinks: async () => {
    try {
      const r = await fetch(`${ONTOLOGY_API}/object-types/links/all`, {
        headers: { 'x-tenant-id': getTenantId() },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      set({ links: Array.isArray(data) ? data : [] });
    } catch {
      set({ links: [] });
    }
  },

  pivotToLinked: async (option, via) => {
    const { selectedTypeId, filters, objectTypes, trail, result } = get();
    if (!selectedTypeId || option.disabledReason) return;
    const nameOf = (id: string) => {
      const ot = objectTypes.find((o) => o.id === id);
      return ot?.displayName || ot?.name || id;
    };
    set({ pivoting: true, pivotError: null });
    try {
      // Salto 1: valores de enlace del conjunto de origen.
      let { values, truncated } = await collectKeyValues(selectedTypeId, filters, option.anchorField);
      // Salto 2 (many-to-many): de la tabla intermedia, los valores del otro lado.
      if (option.through) {
        const hop = await collectKeyValues(
          option.through.objectTypeId,
          [buildPivotFilter('through', option.through.anchorKey, values, nameOf(selectedTypeId))],
          option.through.matchKey,
        );
        values = hop.values;
        truncated = truncated || hop.truncated;
      }
      const sourceName = nameOf(selectedTypeId);
      const last = trail[trail.length - 1];
      const current: PivotCrumb = {
        ...(last ?? {}),
        typeId: selectedTypeId,
        typeName: sourceName,
        filters,
        count: result?.total ?? last?.count ?? null,
      };
      const pivotFilter = buildPivotFilter(
        `pivot-${Date.now()}`, option.matchField, values, sourceName,
      );
      const next: PivotCrumb = {
        typeId: option.targetTypeId,
        typeName: nameOf(option.targetTypeId),
        filters: [pivotFilter],
        via,
        count: null,
        truncated,
      };
      await get().selectObjectType(option.targetTypeId);
      set({
        filters: [pivotFilter], aggregate: null, groupBy: null,
        trail: pushPivotCrumb(trail, current, next),
      });
      await get().runQuery(100, 0);
      const total = get().result?.total ?? null;
      set((s) => ({
        trail: s.trail.map((c, i) => (i === s.trail.length - 1 ? { ...c, count: total } : c)),
      }));
    } catch (e) {
      set({ pivotError: e instanceof Error ? e.message : String(e) });
    } finally {
      set({ pivoting: false });
    }
  },

  restoreCrumb: async (index) => {
    const { trail } = get();
    const crumb = trail[index];
    if (!crumb) return;
    await get().selectObjectType(crumb.typeId);
    set({
      filters: crumb.filters, aggregate: null, groupBy: null,
      trail: truncateTrail(trail, index), pivotError: null,
    });
    await get().runQuery(100, 0);
  },

  clearTrail: () => set({ trail: [], pivotError: null }),
}));
